<!-- src/lib/components/CrossRepoBoard.svelte -->
<!--
  Merged kanban view across multiple projects/repos. Controlled by a
  ViewConfig whose repo_ids field is either ["*"] (all repos) or a list
  of repo IDs. Columns are unified by name: Todo, In Progress, Done.
  Each card shows a repo badge so the user can tell which project it
  belongs to.
-->
<script lang="ts">
  import { pmStore, hydrateBoardDetail } from '../stores/pm.svelte.ts';
  import type { ViewConfig } from '../types.ts';
  import CardItem from './CardItem.svelte';
  import { onMount } from 'svelte';

  interface Props {
    viewConfig: ViewConfig;
    onOpenCard: (id: string) => void;
  }
  let { viewConfig, onOpenCard }: Props = $props();

  // Parse the repo_ids JSON string
  const repoIds = $derived((() => {
    try {
      return JSON.parse(viewConfig.repo_ids) as string[];
    } catch {
      return ['*'];
    }
  })());

  const isAllRepos = $derived(
    repoIds.length === 1 && repoIds[0] === '*'
  );

  // Projects that match the view's repo scope
  const matchingProjects = $derived(
    isAllRepos
      ? pmStore.projects
      : pmStore.projects.filter(p => p.repo_id !== null && repoIds.includes(p.repo_id))
  );

  // Helper: get repo name for a project
  function repoName(repoId: string | null): string {
    if (!repoId) return '—';
    return pmStore.repos.find(r => r.id === repoId)?.name ?? repoId.slice(0, 8);
  }

  // Hydrate board details for any matching project whose board isn't loaded yet
  onMount(async () => {
    for (const project of matchingProjects) {
      // Find board(s) for this project
      const projectBoards = pmStore.boards.filter(b => b.project_id === project.id);
      for (const board of projectBoards) {
        if (!pmStore.boardDetails[board.id]) {
          await hydrateBoardDetail(board.id);
        }
      }
    }
  });

  // Canonical column names — order matters for display
  const CANONICAL_COLS = ['Todo', 'In Progress', 'Done'];

  // Build merged columns: for each canonical name, collect cards from all
  // matching projects' boards whose column name matches (case-insensitive).
  type MergedCard = {
    card: import('../types.ts').Card;
    repoBadge: string;
    columnName: string;
  };

  type MergedColumn = {
    name: string;
    cards: MergedCard[];
    extraCols: string[];  // names of non-canonical cols folded into this one
  };

  const mergedColumns = $derived((() => {
    const colMap = new Map<string, MergedCard[]>();
    const extraMap = new Map<string, Set<string>>();

    for (const colName of CANONICAL_COLS) {
      colMap.set(colName, []);
      extraMap.set(colName, new Set());
    }

    for (const project of matchingProjects) {
      const badge = repoName(project.repo_id);
      const projectBoards = pmStore.boards.filter(b => b.project_id === project.id);

      for (const board of projectBoards) {
        const detail = pmStore.boardDetails[board.id];
        if (!detail) continue;

        for (const col of detail.columns) {
          // Map column to canonical bucket
          const nameLower = col.name.toLowerCase();
          let bucket: string;
          if (nameLower.includes('progress') || nameLower.includes('doing') || nameLower.includes('active')) {
            bucket = 'In Progress';
          } else if (nameLower.includes('done') || nameLower.includes('complete') || nameLower.includes('closed') || nameLower.includes('finish')) {
            bucket = 'Done';
          } else {
            // Default: Todo (covers "todo", "backlog", "open", etc.)
            bucket = 'Todo';
          }

          const bucketCards = colMap.get(bucket)!;
          if (col.name !== bucket) {
            extraMap.get(bucket)!.add(col.name);
          }

          for (const card of col.cards) {
            bucketCards.push({ card, repoBadge: badge, columnName: col.name });
          }
        }
      }
    }

    return CANONICAL_COLS.map(name => ({
      name,
      cards: colMap.get(name) ?? [],
      extraCols: [...(extraMap.get(name) ?? [])],
    })) as MergedColumn[];
  })());

  const COLUMN_COLORS: Record<string, string> = {
    'Todo':        'oklch(0.55 0.04 275)',
    'In Progress': 'oklch(0.68 0.22 265)',
    'Done':        'oklch(0.68 0.18 145)',
  };
</script>

<div class="cross-board-wrapper">
  <div class="cross-board-header">
    <span class="view-label">{viewConfig.name}</span>
    <span class="view-type-chip">{isAllRepos ? 'All repos' : `${matchingProjects.length} project${matchingProjects.length === 1 ? '' : 's'}`}</span>
  </div>

  <div class="board">
    {#each mergedColumns as col (col.name)}
      <div class="column">
        <header class="col-header" style="--col-color: {COLUMN_COLORS[col.name] ?? 'oklch(0.55 0.04 275)'}">
          <div class="col-title-row">
            <span class="col-indicator"></span>
            <span class="col-name">{col.name}</span>
            <span class="col-count">{col.cards.length}</span>
          </div>
          {#if col.extraCols.length > 0}
            <span class="extra-cols" title={col.extraCols.join(', ')}>+{col.extraCols.length}</span>
          {/if}
        </header>

        <div class="card-list">
          {#if col.cards.length === 0}
            <p class="empty-col">No cards</p>
          {:else}
            {#each col.cards as { card, repoBadge, columnName } (card.id)}
              <CardItem
                {card}
                onOpen={onOpenCard}
                {columnName}
                {repoBadge}
              />
            {/each}
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  .cross-board-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .cross-board-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.6rem 1.25rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    background: rgba(255,255,255,0.01);
    flex-shrink: 0;
  }

  .view-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: oklch(0.88 0.06 310);
  }

  .view-type-chip {
    font-size: 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.68 0.18 265 / 0.85);
    background: oklch(0.68 0.18 265 / 0.10);
    border: 1px solid oklch(0.68 0.18 265 / 0.25);
    padding: 0.08rem 0.45rem;
    border-radius: 4px;
  }

  .board {
    display: flex;
    gap: 1rem;
    padding: 1.25rem;
    overflow-x: auto;
    flex: 1;
    align-items: flex-start;
    background:
      radial-gradient(ellipse at 18% 55%, oklch(0.28 0.18 310 / 0.055) 0%, transparent 60%),
      radial-gradient(ellipse at 82% 28%, oklch(0.28 0.16 265 / 0.045) 0%, transparent 60%);
  }

  .column {
    flex-shrink: 0;
    width: 300px;
    background: linear-gradient(180deg,
      oklch(0.13 0.02 275) 0%,
      oklch(0.10 0.02 275) 100%);
    border: 1px solid oklch(0.30 0.05 275 / 0.18);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow: hidden;
    position: relative;
    contain: layout style paint;
  }

  .column::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background: radial-gradient(
      ellipse 80% 40% at 50% 0%,
      var(--col-color, oklch(0.55 0.04 275)) 0%,
      transparent 72%);
    opacity: 0.12;
  }

  .col-header {
    padding: 0.75rem 0.875rem 0.5rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    border-top: 2px solid var(--col-color);
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 1;
  }

  .col-title-row {
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }

  .col-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--col-color);
    box-shadow: 0 0 6px var(--col-color);
    flex-shrink: 0;
  }

  .col-name {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.65);
    font-weight: 500;
    letter-spacing: 0.01em;
  }

  .col-count {
    font-size: 0.68rem;
    background: rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.4);
    border-radius: 8px;
    padding: 0.05rem 0.4rem;
    font-family: 'JetBrains Mono', monospace;
  }

  .extra-cols {
    font-size: 0.6rem;
    color: oklch(0.55 0.04 310 / 0.5);
    font-family: 'JetBrains Mono', monospace;
    cursor: help;
  }

  .card-list {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.25rem 0.625rem 0.75rem;
    min-height: 40px;
    position: relative;
    z-index: 1;
  }

  .empty-col {
    font-size: 0.72rem;
    color: oklch(0.45 0.04 310 / 0.4);
    text-align: center;
    padding: 1rem 0;
    margin: 0;
  }
</style>
